import type { BrandingConfig, CustomFieldDefinition, LogRetentionConfig, LoginMethodConfig } from "./types";

export interface SettingValues {
  "login-methods": LoginMethodConfig;
  "custom-fields": CustomFieldDefinition[];
  branding: BrandingConfig;
  "log-retention": LogRetentionConfig;
}

export type SettingKey = keyof SettingValues;
export type SettingValue<K extends SettingKey> = SettingValues[K];

export const SETTING_KEYS = [
  "login-methods",
  "custom-fields",
  "branding",
  "log-retention",
] as const satisfies readonly SettingKey[];

export function isSettingKey(v: string): v is SettingKey {
  return (SETTING_KEYS as readonly string[]).includes(v);
}

export const DEFAULT_LOGIN_METHODS: LoginMethodConfig = {
  emailPassword: {
    enabled: true,
    allowSignUp: true,
    requireEmailVerification: true,
    allowedEmailDomains: [],
    minPasswordLength: 12,
  },
  social: { google: { enabled: false }, microsoft: { enabled: false } },
  passkey: { enabled: false },
  mfa: { policy: "required_for_admins", methods: ["totp", "email_otp"] },
  session: { maxAgeDays: 7 },
  phone: { enabled: true, otpSignIn: true },
};

/** Phone sign-in options with defaults for settings stored before the option existed. */
export function phoneLoginConfig(lm: LoginMethodConfig): { enabled: boolean; otpSignIn: boolean } {
  return lm.phone ?? { enabled: true, otpSignIn: true };
}

export const DEFAULT_BRANDING: BrandingConfig = { productName: "Auth Platform", primaryColor: "#009be5" };

export function defaultSettings(env: { logRetentionDays?: number } = {}): SettingValues {
  return {
    "login-methods": structuredClone(DEFAULT_LOGIN_METHODS),
    "custom-fields": [],
    branding: structuredClone(DEFAULT_BRANDING),
    "log-retention": { days: env.logRetentionDays ?? 365 },
  };
}

/** Email-domain allow-list check. Empty list = any domain allowed. */
export function isEmailDomainAllowed(email: string, allowed: readonly string[]): boolean {
  if (allowed.length === 0) return true;
  const domain = email.trim().toLowerCase().split("@").pop() ?? "";
  return allowed.some((d) => {
    const want = d.trim().toLowerCase().replace(/^@/, "");
    return domain === want || (want.startsWith("*.") && domain.endsWith(want.slice(1)));
  });
}
